// src/components/Layout.js — Rebaz 餐廳營運台 殼
//
// 風格對齊 toy-app frontend/src/components/Layout.js (可收合 mini-variant 側邊欄 +
// 白色 offset AppBar + profile menu),但精簡到餐廳營運需要的選單。
// 品牌做「每間餐廳自己的 logo + 店名」(取自 companyService.getCompanyById,
// 餐廳在「公司設定」自行修改) — 比照 FMP 把 tenant 名稱/logo 可客製,但 FMP 只進 PDF,
// 這裡進側邊欄表頭。
// 選單由 MENU_ITEMS 驅動,plan-gated (hasFeature);P2/P3/P5 取消註解對應項目即可。
import React, { useState, useEffect, useMemo, useRef, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsightsIcon from '@mui/icons-material/Insights';
import BusinessIcon from '@mui/icons-material/Business';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';        // P2 線上訂位設定
import EventSeatIcon from '@mui/icons-material/EventSeat';      // P2 訂單管理（訂位/外帶）
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant'; // 106 桌位管理（自 console 移出）
import MenuBookIcon from '@mui/icons-material/MenuBook';        // 106 菜單管理
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import QuizIcon from '@mui/icons-material/Quiz';   // P3 通話紀錄
// 語音對話腳本 / 外撥活動 暫時隱藏 (2026-07-14 產品決定) — 需要時連同下方選單一起取消註解
// import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'; // P3 語音對話腳本
// import CampaignIcon from '@mui/icons-material/Campaign';        // P3 外撥活動
// P5 (排隊): import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

import { hasFeature } from '../utils/features';
import companyService from '../services/companyService';
import restaurantService from '../services/restaurantService';
import PushNotificationToggle from './PushNotificationToggle';

const drawerWidth = 240;
const miniDrawerWidth = 64;
const BRAND_CACHE_KEY = 'bookingCompanyBrand';
// 未讀新訂位徽章（T-031）— 每台裝置各自記「上次看過訂單管理」的時間
const RESERVATIONS_SEEN_KEY = 'bookingReservationsSeenAt';
const RESERVATIONS_PATH = '/restaurant/reservations';

// ── 全域字級 / 縮放 (T-024) ─────────────────────────────
// navbar 的「A」控制調整整個內容區的 zoom(navbar 本身不縮)。用 CSS zoom：
// 內容整體放大、RWD 斷點仍看真實視窗寬(卡片↔表格不受影響)、width:100% 子元素自動重排不爆版。
// 分裝置(localStorage)。
// 例外:整頁式 console(m:-3 + calc(100vh) + overflow:hidden)無法吃 wrapper zoom(zoom 會把
// 100vh/負 margin 一起放大 → 破版),這類頁面用 <Layout disableContentZoom>,並監聽
// FONT_SCALE_EVENT 廣播、自行縮放內文(見 RestaurantReservationsPage)。
const FONT_SCALE_KEY = 'bookingFontScale';
const FONT_SCALE_OPTIONS = [
  { value: 1, label: '標準', glyph: 12 },
  { value: 1.18, label: '大', glyph: 15 },
  { value: 1.38, label: '特大', glyph: 18 },
];
const DEFAULT_FONT_SCALE = 1;

// 全域字級變更時廣播,讓「自行縮放」的整頁式 console(m:-3 + 100vh,無法吃 wrapper zoom)同步。
export const FONT_SCALE_EVENT = 'booking:fontScaleChanged';

export const loadStoredFontScale = () => {
  try {
    const v = parseFloat(localStorage.getItem(FONT_SCALE_KEY));
    return FONT_SCALE_OPTIONS.some((o) => o.value === v) ? v : DEFAULT_FONT_SCALE;
  } catch {
    return DEFAULT_FONT_SCALE;
  }
};

// 頁面可用此 context 取得目前縮放倍率(給 portal 出去的表單自行套 zoom)。
export const FontScaleContext = createContext(1);
export const useFontScale = () => useContext(FontScaleContext);

// navbar 三段「A」字級控制(放帳號左邊)
const FontScaleControl = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: { xs: 1, sm: 1.5 } }}>
    <Typography
      component="span"
      aria-hidden
      sx={{ fontWeight: 900, color: 'primary.main', fontSize: 18, lineHeight: 1, display: { xs: 'none', sm: 'block' } }}
    >
      A
    </Typography>
    <Box sx={{ display: 'inline-flex', border: '1.5px solid #cdd6e2', borderRadius: 1.5, overflow: 'hidden' }}>
      {FONT_SCALE_OPTIONS.map((opt, i) => {
        const on = value === opt.value;
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            onClick={() => onChange(opt.value)}
            aria-label={`字級：${opt.label}`}
            aria-pressed={on}
            title={opt.label}
            sx={{
              border: 'none',
              borderLeft: i === 0 ? 'none' : '1px solid #e6ebf1',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 800,
              lineHeight: 1,
              px: 1,
              py: 0.5,
              fontSize: opt.glyph,
              bgcolor: on ? 'primary.main' : '#fff',
              color: on ? '#fff' : '#5b6b7f',
              transition: 'background-color .15s',
              '&:hover': { bgcolor: on ? 'primary.main' : '#eef2f7' },
            }}
          >
            A
          </Box>
        );
      })}
    </Box>
  </Box>
);

// 選單定義 — P2/P3/P5 在這裡加項目。
//   feature: null = 永遠顯示;否則 hasFeature(feature) 控制 (plan-gated)
//   adminOnly: true = 只有 admin / super_admin 可見
const MENU_ITEMS = [
  { key: 'dashboard', label: '營運總覽', icon: <InsightsIcon />, path: '/dashboard', feature: null },

  // ── P2 線上訂位核心 ──────────────────────────────
  // 訂單管理 console (Plan 082 + 106 外帶) — 所有員工可見（內外勤都要看訂位/外帶），plan-gated。
  { key: 'reservations', label: '訂單管理（訂位/外帶）', icon: <EventSeatIcon />, path: '/restaurant/reservations', feature: 'reservationEnabled' },
  // 桌位管理（106 §1.2：自 console 移出成獨立頁；桌位 CRUD 屬設定性質）
  { key: 'tables', label: '桌位管理', icon: <TableRestaurantIcon />, path: '/restaurant/tables', feature: 'reservationEnabled' },
  // 菜單管理（Plan 106 實作、098 共用）— 菜單/價格屬設定性質，adminOnly
  { key: 'menu', label: '菜單管理', icon: <MenuBookIcon />, path: '/menu-settings', feature: 'reservationEnabled', adminOnly: true },
  // 線上訂位設定 (Plan 090) — 餐廳自助設定公開訂位頁 (/r/:slug)。adminOnly。
  { key: 'web-booking', label: '線上訂位設定', icon: <LanguageIcon />, path: '/web-booking-settings', feature: 'reservationEnabled', adminOnly: true },
  // Q&A 管理 (T-038) — 語音 FAQ 的內容維護。餐廳自己改「開瓶費多少」不用找我們。
  // adminOnly：FAQ 內容等同對外話術，不該讓一般員工改。
  { key: 'faq', label: 'Q&A管理', icon: <QuizIcon />, path: '/faq', feature: 'faqEnabled', adminOnly: true },

  // ── P3 語音 ──────────────────────────────────────
  { key: 'call-logs', label: '通話紀錄', icon: <PhoneInTalkIcon />, path: '/call-logs', feature: 'voiceEnabled' },
  // 語音對話腳本 / 外撥活動 — 暫時隱藏 (2026-07-14 產品決定)，需要時取消註解即可
  // { key: 'voice-flows', label: '語音對話腳本', icon: <RecordVoiceOverIcon />, path: '/voice-flows', feature: 'voiceEnabled', adminOnly: true },
  // { key: 'outbound', label: '外撥活動', icon: <CampaignIcon />, path: '/outbound-campaigns', feature: 'voiceEnabled', adminOnly: true },

  // ── P5 排隊 / 候位 (Plan 092) ────────────────────
  // { key: 'queue', label: '排隊候位', icon: <ConfirmationNumberIcon />, path: '/queue', feature: 'queueEnabled' },

  // ── 設定 ─────────────────────────────────────────
  { key: 'company', label: '公司設定', icon: <BusinessIcon />, path: '/company/profile', feature: null },
];

// disableContentZoom: 整頁式 console(m:-3 + calc(100vh) + overflow:hidden)無法吃 wrapper zoom
//   (zoom 會把 100vh/負 margin 一起放大 → 破版),這類頁面自行縮放內文,故關閉 wrapper zoom。
const Layout = ({ children, disableContentZoom = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });
  // 每間餐廳的品牌 (logo + 店名);先用快取避免閃爍,進站再從後端刷新
  const [brand, setBrand] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(BRAND_CACHE_KEY) || '{}');
    } catch {
      return {};
    }
  });
  // 小螢幕（<lg 1200px，多為 7-10 吋平板）預設收成迷你圖示欄，讓 console 有更多寬度；桌機維持展開。可隨時用 < 鍵切換。
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= theme.breakpoints.values.lg);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // 推播開關的操作結果提示（Snackbar 需在帳號選單關掉後仍看得到，故提到 Layout 層）
  const [pushSnack, setPushSnack] = useState({ open: false, message: '', severity: 'success' });

  // ── 未讀新訂位徽章（T-031）─────────────────────────────
  const companyId = useMemo(() => user?.companyId || user?.company_id || null, [user]);
  const [newResCount, setNewResCount] = useState(0);
  const resSocketRef = useRef(null);

  // 首載：算自「上次看過訂單管理」後的新訂位數（首次無記錄→設 now→0，不嚇人）
  useEffect(() => {
    if (!companyId) return;
    let seenAt = localStorage.getItem(RESERVATIONS_SEEN_KEY);
    if (!seenAt) {
      seenAt = new Date().toISOString();
      localStorage.setItem(RESERVATIONS_SEEN_KEY, seenAt);
    }
    // 一進來就在看板頁 → 直接視為已讀
    if (window.location.pathname.startsWith(RESERVATIONS_PATH)) {
      localStorage.setItem(RESERVATIONS_SEEN_KEY, new Date().toISOString());
      setNewResCount(0);
      return;
    }
    restaurantService.getUnseenReservationCount(seenAt)
      .then((body) => { if (body?.success) setNewResCount(body.data?.count || 0); })
      .catch(() => {});
  }, [companyId]);

  // 即時：socket 收到新訂位 → 徽章 +1（正在看板頁則視為已讀、不加）
  useEffect(() => {
    if (!companyId) return undefined;
    const socketUrl = process.env.REACT_APP_SOCKET_URL
      || process.env.REACT_APP_API_URL?.replace('/api', '')
      || window.location.origin;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    try {
      const socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true, reconnectionAttempts: 5, reconnectionDelay: 2000,
      });
      const joinRoom = () => socket.emit('join_company', { companyId, userId: localStorage.getItem('userId') || 'anonymous' });
      socket.on('connect', joinRoom);
      socket.on('reconnect', joinRoom);
      socket.on('reservation.created', (payload) => {
        if (!payload?.reservation) return;
        // 正在看板頁 → 他們看得到，視為已讀（更新 seenAt、不加徽章）
        if (window.location.pathname.startsWith(RESERVATIONS_PATH)) {
          localStorage.setItem(RESERVATIONS_SEEN_KEY, new Date().toISOString());
          return;
        }
        setNewResCount((n) => n + 1);
      });
      resSocketRef.current = socket;
    } catch (e) { /* socket 失敗不擋主流程 */ }
    return () => { if (resSocketRef.current) { resSocketRef.current.disconnect(); resSocketRef.current = null; } };
  }, [companyId]);

  // 進到訂單管理看板 → 標記已讀、徽章歸零
  useEffect(() => {
    if (location.pathname.startsWith(RESERVATIONS_PATH)) {
      localStorage.setItem(RESERVATIONS_SEEN_KEY, new Date().toISOString());
      setNewResCount(0);
    }
  }, [location.pathname]);

  // 全域字級 (T-024) — 分裝置儲存
  const [fontScale, setFontScale] = useState(loadStoredFontScale);
  const changeFontScale = (v) => {
    setFontScale(v);
    try {
      localStorage.setItem(FONT_SCALE_KEY, String(v));
    } catch {
      /* localStorage 不可用時忽略 */
    }
    // 廣播給 disableContentZoom 的整頁式 console(自行縮放內文)即時同步
    try {
      window.dispatchEvent(new CustomEvent(FONT_SCALE_EVENT, { detail: v }));
    } catch {
      /* 舊環境無 CustomEvent 時忽略 */
    }
  };

  const currentDrawerWidth = sidebarOpen ? drawerWidth : miniDrawerWidth;
  const widthTransition = theme.transitions.create(['width', 'margin-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  });

  // 進站:① 從 /api/auth/me 刷新 planSettings (hasFeature 才準);② 取公司品牌
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

    // ① planSettings
    fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((meData) => {
        if (!meData) return;
        const updated = { ...storedUser, planSettings: meData.planSettings || null };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
      })
      .catch(() => {});

    // ② 公司品牌 (logo + 店名) — 餐廳在「公司設定」改的同一份資料
    const companyId = storedUser.companyId || storedUser.company_id;
    if (companyId) {
      companyService
        .getCompanyById(companyId)
        .then((res) => {
          const c = res?.data ?? res ?? {};
          const next = { name: c.name || '', logoUrl: c.logoUrl || '' };
          setBrand(next);
          localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(next));
        })
        .catch(() => {});
    }
  }, [navigate]);

  // 公司設定存檔後即時更新側邊欄品牌 (不用刷新整頁) — CompanyFormPage 存檔成功時
  // 會 dispatch 'company:brandUpdated' 帶上最新 name/logoUrl。
  useEffect(() => {
    const onBrandUpdate = (e) => {
      const next = { name: e.detail?.name || '', logoUrl: e.detail?.logoUrl || '' };
      setBrand(next);
      localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(next));
    };
    window.addEventListener('company:brandUpdated', onBrandUpdate);
    return () => window.removeEventListener('company:brandUpdated', onBrandUpdate);
  }, []);

  const isAdmin =
    user?.role === 'admin' ||
    user?.role === 'super_admin' ||
    user?.role === 'agency_admin';

  const visibleItems = MENU_ITEMS.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.feature && !hasFeature(item.feature)) return false;
    return true;
  });

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    [
      'authToken', 'token', 'accessToken', 'jwt', 'jwtToken',
      'user', 'companyId', 'companyStatus', 'planName',
      BRAND_CACHE_KEY,
      'agencySelectedCompanyId', 'agencySelectedCompanyName',
    ].forEach((k) => localStorage.removeItem(k));
    window.dispatchEvent(new Event('authChange'));
    navigate('/login');
  };

  const brandName = brand.name || '餐廳營運台';
  const userLabel = user?.name || user?.username || user?.email || '';
  const userInitial = (userLabel || 'U').charAt(0).toUpperCase();

  // ── 側邊欄內容 (expanded: 是否展開顯示文字) ──
  const drawerContent = (expanded) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowX: 'hidden' }}>
      {/* 表頭 = 該餐廳自己的 logo + 店名 */}
      <Toolbar sx={{ gap: 1, minHeight: 64, justifyContent: expanded ? 'flex-start' : 'center', px: expanded ? 2 : 1 }}>
        <Avatar
          src={brand.logoUrl || undefined}
          alt={brandName}
          sx={{ width: 34, height: 34, bgcolor: 'primary.main' }}
        >
          {!brand.logoUrl && <RestaurantMenuIcon sx={{ fontSize: 20 }} />}
        </Avatar>
        {expanded && (
          <Typography
            variant="subtitle1"
            noWrap
            color="primary"
            fontWeight="bold"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {brandName}
          </Typography>
        )}
      </Toolbar>
      <Divider />

      <List sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {visibleItems.map((item) => {
          const selected =
            location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          return (
            <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
              <Tooltip title={expanded ? '' : item.label} placement="right">
                <ListItemButton
                  onClick={() => handleNav(item.path)}
                  selected={selected}
                  sx={{
                    minHeight: 48,
                    justifyContent: expanded ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 3 : 'auto', justifyContent: 'center' }}>
                    {item.key === 'reservations' && newResCount > 0 ? (
                      <Badge badgeContent={newResCount} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {expanded && <ListItemText primary={item.label} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* 收合/展開 (僅桌面) */}
      <Divider sx={{ display: { xs: 'none', sm: 'block' } }} />
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', p: 1 }}>
        <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} size="small">
          {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <FontScaleContext.Provider value={fontScale}>
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* 頂列 — 白底,offset 到側邊欄右側 */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: widthTransition,
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="開啟選單"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            {/* 手機版側邊欄是收起的抽屜 → 把未讀新訂位紅點顯示在漢堡鈕上，不用展開就看得到 */}
            <Badge badgeContent={newResCount} color="error" max={99} invisible={newResCount === 0}>
              <MenuIcon />
            </Badge>
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />

          {/* 全域字級控制 — 帳號左邊 (T-024) */}
          <FontScaleControl value={fontScale} onChange={changeFontScale} />

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1.5, display: { xs: 'none', sm: 'block' } }}>
                {userLabel}
              </Typography>
              <IconButton edge="end" onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 14 }}>
                  {userInitial}
                </Avatar>
              </IconButton>
            </Box>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
              {brand.name && (
                <>
                  <Typography variant="caption" color="text.secondary">公司</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{brand.name}</Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary">{userLabel}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/company/profile'); }}>
              <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
              公司設定
            </MenuItem>
            <Divider />
            {/* 新訂位推播開關（有新訂位 → 推到此裝置鎖屏/桌面，T-031） */}
            <PushNotificationToggle
              onNotify={(message, severity = 'success') => setPushSnack({ open: true, message, severity })}
              onAfterAction={() => setAnchorEl(null)}
            />
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              登出
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* 側邊欄 — 桌面常駐(可收合),手機抽屜 */}
      <Box
        component="nav"
        sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 }, transition: widthTransition }}
        aria-label="主選單"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent(true)}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              overflowX: 'hidden',
              transition: widthTransition,
            },
          }}
        >
          {drawerContent(sidebarOpen)}
        </Drawer>
      </Box>

      {/* 主內容 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // 手機需給 main 明確寬度(xs:100%)+ minWidth:0,否則內層 zoom 放大時
          // main 會被撐寬 → body 水平溢出(桌機 sm+ 本來就有 calc 明確寬度故無此問題)。
          width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
          minWidth: 0,
          transition: widthTransition,
          marginTop: '64px',
          bgcolor: '#f5f5f5',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {/* 內層 zoom 只縮放頁面內容(不含 navbar / 側欄);width:100% 子元素自動重排,不爆版。
            fontScale===1 或整頁式 console(disableContentZoom)時不設 zoom
            (後者會把 100vh/負 margin 一起放大 → 破版,改由該頁自行縮放內文)。
            overflowX:auto:放大後若某頁有非流式(固定寬/不折行)元素,橫向捲動只發生在
            內容區內,不會讓整個 body 產生水平捲動。 */}
        <Box sx={(disableContentZoom || fontScale === 1) ? undefined : { zoom: fontScale, overflowX: 'auto' }}>
          {children}
        </Box>
      </Box>

      {/* 推播開關操作結果提示 */}
      <Snackbar
        open={pushSnack.open}
        autoHideDuration={4000}
        onClose={() => setPushSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPushSnack((s) => ({ ...s, open: false }))}
          severity={pushSnack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {pushSnack.message}
        </Alert>
      </Snackbar>
    </Box>
    </FontScaleContext.Provider>
  );
};

export default Layout;
